import type {
  ColumnId,
  PinnableTableProps,
  PinnedTableColumn,
} from '@dixa/lib-shared-habla';
import type { Column, Table } from '@tanstack/react-table';
import type { SearchConversationEnriched } from 'lib-search-types';

import type { ConversationsTableFeatures } from './conversationsTable.columns';

/**
 * Whether the table is at least as wide as its column sizes and scrolls
 * sideways, which a table the user can customise does so its columns keep the
 * sizes they're pinned and resized to.
 */
export const getIsConversationsTableScrollable = (
  table: Table<ConversationsTableFeatures, SearchConversationEnriched>,
): boolean => Boolean(table.options.meta?.layout);

/** Returns the id that Habla sorts and pins the column by, which is the field it's sorted by when it can be sorted. */
export const getConversationsTableColumnId = (
  column: Column<ConversationsTableFeatures, SearchConversationEnriched>,
): ColumnId => column.columnDef.meta?.sortBy ?? column.id;

/** Returns the column's header text, which a column with a rendered header, like the actions, doesn't have. */
export const getConversationsTableColumnHeader = (
  column: Column<ConversationsTableFeatures, SearchConversationEnriched>,
): string | undefined =>
  typeof column.columnDef.header === 'string'
    ? column.columnDef.header
    : undefined;

const getPinnedColumn = (
  column: Column<ConversationsTableFeatures, SearchConversationEnriched>,
): PinnedTableColumn => ({
  columnId: getConversationsTableColumnId(column),
  width: column.getSize(),
});

/** Returns the columns that stay in view when the table scrolls sideways, which only a table the user can customise pins. */
export const getConversationsTablePinningProps = (
  table: Table<ConversationsTableFeatures, SearchConversationEnriched>,
): PinnableTableProps | undefined => {
  if (!table.options.meta?.layout) {
    return undefined;
  }

  return {
    startColumns: table.getStartVisibleLeafColumns().map(getPinnedColumn),
    endColumns: table.getEndVisibleLeafColumns().map(getPinnedColumn),
  };
};
